import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react'
import * as apiClient from './api'
import type { ApiUser } from './api'

interface AuthState {
  user: ApiUser | null
  ready: boolean
  signIn: (email: string, password: string) => Promise<void>
  signOut: () => Promise<void>
}

const AuthContext = createContext<AuthState | null>(null)

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<ApiUser | null>(null)
  const [ready, setReady] = useState(false)

  // On load, try to trade the refresh cookie for a session before rendering
  // routes, so a reload does not bounce the user to the login page.
  useEffect(() => {
    let cancelled = false
    void (async () => {
      const token = await apiClient.refreshAccessToken()
      if (token) {
        const me = await apiClient.fetchMe().catch(() => null)
        if (!cancelled) setUser(me)
      }
      if (!cancelled) setReady(true)
    })()
    return () => {
      cancelled = true
    }
  }, [])

  // Access tokens last 15 minutes; renew well before they expire so long
  // browsing sessions never hit a 401 mid-action.
  useEffect(() => {
    if (!user) return
    const timer = setInterval(() => void apiClient.refreshAccessToken(), 10 * 60 * 1000)
    return () => clearInterval(timer)
  }, [user])

  const signIn = useCallback(async (email: string, password: string) => {
    setUser(await apiClient.login(email, password))
  }, [])

  const signOut = useCallback(async () => {
    await apiClient.logout()
    setUser(null)
  }, [])

  const value = useMemo<AuthState>(
    () => ({ user, ready, signIn, signOut }),
    [user, ready, signIn, signOut],
  )

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export function useAuth(): AuthState {
  const context = useContext(AuthContext)
  if (!context) throw new Error('useAuth must be used inside AuthProvider')
  return context
}
